/**
 * Task Form Modal
 * Create and edit tasks with subtasks
 * Matches Figma design modal styling
 */

import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ACCENT_BUTTON } from '@/components/ui/accent';
import {
  Task,
  Subtask,
  TaskType,
  TaskPriority,
  TaskStatus,
  tasksAPI,
  TASK_TYPE_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS
} from '@/services/api/tasks';
import { userManagementAPI, OrganizationUser } from '@/services/api/userManagement';
import { useNotificationTriggers } from '@/hooks/useNotificationTriggers';
import { useAuth } from '@/contexts/AuthContext';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { isValidTimeZone, toZonedInputs, zonedInputToIso } from '@/utils/zonedTime';
import { TimeZoneSelect } from '@/components/ui/TimeZoneSelect';

/**
 * TRUS-697: the due date + optional due time, read in the task's own zone. A
 * due time is sent as `due_at` (UTC ISO) with `timezone`; clearing the time on
 * an edit sends `due_at: null`.
 */
export function taskDueFields(
  deadline: string,
  dueTime: string,
  zone: string,
  hadDueTime: boolean,
): Pick<Partial<Task>, 'due_at' | 'timezone'> {
  if (deadline && /^\d{2}:\d{2}$/.test(dueTime)) {
    const iso = zonedInputToIso(`${deadline}T${dueTime}`, zone);
    if (iso) return { due_at: iso, timezone: zone };
  }
  return hadDueTime ? { due_at: null } : {};
}

interface TaskFormProps {
  memberId: number;
  task?: Task | null;
  onClose: () => void;
  onSaved: () => void;
  /** TRUS-762: a NEW task is filed under this case. Ignored when editing. */
  caseEpisodeId?: number | null;
  /**
   * 'dialog' (default) mounts the form in a Radix dialog. 'page' renders the
   * same form in place, without its own title, for a host screen that already
   * has a header and a way back (the member's Activities workspace, TRUS-762).
   */
  variant?: 'dialog' | 'page';
}

export default function TaskForm({ memberId, task, onClose, onSaved, caseEpisodeId, variant = 'dialog' }: TaskFormProps) {
  const { t } = useAppTranslation();
  const isEditing = !!task;
  const { user } = useAuth();
  const { notifyTaskAssignment } = useNotificationTriggers();

  // Form state
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [taskType, setTaskType] = useState<TaskType>(task?.task_type || 'task');
  const [status, setStatus] = useState<TaskStatus>(task?.status || 'not_started');
  const [priority, setPriority] = useState<TaskPriority>(task?.priority || 'medium');
  const [assignedTo, setAssignedTo] = useState<number | undefined>(task?.assigned_to);
  // TRUS-697: an existing due time is shown in the task's own zone.
  const userTimezone = useUserTimezone();
  const initialZone = isValidTimeZone(task?.timezone) ? (task!.timezone as string) : userTimezone;
  const initialDue = task?.due_at ? toZonedInputs(task.due_at, initialZone) : null;
  const [deadline, setDeadline] = useState(initialDue?.date || task?.deadline || '');
  const [dueTime, setDueTime] = useState(initialDue?.time || '');
  const [taskZone, setTaskZone] = useState(initialZone);
  const [startDate, setStartDate] = useState(task?.start_date || '');
  const [subtasks, setSubtasks] = useState<Array<Partial<Subtask>>>(
    task?.subtasks || []
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch organization users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const cboId = localStorage.getItem('active_cbo_id');
        if (cboId) {
          const organizationUsers = await userManagementAPI.listUsers(parseInt(cboId));
          setUsers(organizationUsers);
        }
      } catch (err) {
        console.error('Error fetching users for assignment:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleAddSubtask = () => {
    setSubtasks([
      ...subtasks,
      {
        title: '',
        description: '',
        sort_order: subtasks.length,
        is_completed: false,
      }
    ]);
  };

  const handleRemoveSubtask = (index: number) => {
    setSubtasks(subtasks.filter((_, i) => i !== index));
  };

  const handleSubtaskChange = (index: number, field: keyof Subtask, value: any) => {
    setSubtasks(subtasks.map((subtask, i) =>
      i === index ? { ...subtask, [field]: value } : subtask
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);

    try {
      const taskData: Partial<Task> = {
        member_id: memberId,
        title: title.trim(),
        description: description.trim() || undefined,
        task_type: taskType,
        status,
        priority,
        assigned_to: assignedTo,
        deadline: deadline || undefined,
        start_date: startDate || undefined,
        ...taskDueFields(deadline, dueTime, taskZone, !!task?.due_at),
        subtasks: subtasks.filter(s => s.title?.trim()).map((s, index) => ({
          ...s,
          title: s.title!.trim(),
          sort_order: index,
        })),
        ...(!task && caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
      };

      let newTask: Task | undefined;

      if (isEditing && task.id) {
        // Update existing task
        newTask = await tasksAPI.updateTask(task.id, taskData);

        // Trigger notification if assignment changed to a different user
        if (assignedTo && assignedTo !== task.assigned_to) {
          const assignedUser = users.find((u) => u.id === assignedTo);
          if (assignedUser && assignedUser.id !== user?.id) {
            await notifyTaskAssignment({
              taskTitle: title.trim(),
              assignedBy: user?.name || 'Someone',
              dueDate: deadline || undefined,
              taskId: task.id,
              memberId: memberId,
            });
          }
        }
      } else {
        // Create new task
        newTask = await tasksAPI.createTask(taskData);

        // Trigger notification for task assignment
        if (assignedTo) {
          const assignedUser = users.find((u) => u.id === assignedTo);
          if (assignedUser && assignedUser.id !== user?.id) {
            await notifyTaskAssignment({
              taskTitle: title.trim(),
              assignedBy: user?.name || 'Someone',
              dueDate: deadline || undefined,
              taskId: newTask?.id,
              memberId: memberId,
            });
          }
        }
      }

      onSaved();
    } catch (err: any) {
      console.error('Error saving task:', err);
      setError(err.response?.data?.detail || t('tasks.saveFailed', 'Failed to save task'));
    } finally {
      setSaving(false);
    }
  };

  const isPage = variant === 'page';

  const formAndFooter = (
    <>
        {/* Form */}
        <form onSubmit={handleSubmit} className={isPage ? 'p-4 sm:p-6 space-y-4' : 'flex-1 overflow-y-auto p-6 space-y-4'}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tasks.title', 'Title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Identify Target Communities"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Type and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.type', 'Type')}
              </label>
              <select
                value={taskType}
                onChange={(e) => setTaskType(e.target.value as TaskType)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.status', 'Status')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Priority and Assignment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.priority', 'Priority')}
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.assign_to_optional', 'Assign To (Optional)')}
              </label>
              <select
                value={assignedTo || ''}
                onChange={(e) => setAssignedTo(e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={loadingUsers}
              >
                <option value="">{t('tasks.unassigned', 'Unassigned')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              {loadingUsers && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('tasks.loading_users', 'Loading users...')}</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.start_date', 'Start Date')}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.deadline', 'Deadline')}
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="task-deadline"
              />
            </div>
          </div>

          {/* TRUS-697: an optional due time, in the task's own zone. */}
          {deadline && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="task-due-time"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('tasks.dueTime', 'Due time (optional)')}
                </label>
                <input
                  id="task-due-time"
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[44px] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="task-due-time"
                />
              </div>
              <div>
                <label
                  htmlFor="task-timezone"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('time.zone.label', 'Time zone')}
                </label>
                <TimeZoneSelect
                  id="task-timezone"
                  value={taskZone}
                  onChange={setTaskZone}
                  userTimeZone={userTimezone}
                  disabled={!dueTime}
                  data-testid="task-timezone"
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tasks.description', 'Description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Research and pinpoint specific groups or demographics that align with our goals. Analyze community behaviors, interests."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('tasks.subtasks', 'Subtasks')}
              </label>
              <button
                type="button"
                onClick={handleAddSubtask}
                className="flex items-center space-x-2 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 active:scale-95 transition-all min-h-[36px]"
              >
                <Plus className="w-4 h-4" />
                <span>{t('tasks.add_subtask', 'Add Subtask')}</span>
              </button>
            </div>

            {subtasks.length === 0 ? (
              <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.no_subtasks_yet_click_add', 'No subtasks yet. Click "Add Subtask" to create one.')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subtasks.map((subtask, index) => (
                  <div key={index} className="flex items-start space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={subtask.title || ''}
                        onChange={(e) => handleSubtaskChange(index, 'title', e.target.value)}
                        placeholder={t('tasks.subtask_title', 'Subtask title')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[40px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <input
                        type="date"
                        value={subtask.due_date || ''}
                        onChange={(e) => handleSubtaskChange(index, 'due_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[40px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder={t('tasks.due_date', 'Due date')}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubtask(index)}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
                      aria-label={t('tasks.remove_subtask', 'Remove subtask')}
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 border-t border-v2border ${isPage ? 'p-4 sm:px-6' : 'p-6'}`}>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('tasks.cancel', 'Cancel')}
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className={ACCENT_BUTTON}>
            {saving
              ? t('tasks.saving', 'Saving...')
              : isEditing
                ? t('tasks.updateTask', 'Update Task')
                : t('tasks.createTask', 'Create Task')}
          </Button>
        </div>
    </>
  );

  if (isPage) {
    return <div className="rounded-lg border border-v2border bg-surface">{formAndFooter}</div>;
  }

  // A Radix dialog (portal, focus trap, Escape), not a hand-rolled fixed
  // overlay: callers mount it only while it is open, so it is always open.
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
      <div className="max-h-[90vh] flex flex-col">
        {/* Header */}
        <DialogHeader className="p-6 pr-12 border-b border-v2border">
          <DialogTitle className="text-xl md:text-2xl font-bold text-ink">
            {isEditing ? t('tasks.editTask', 'Edit Task') : t('tasks.newTask', 'New Task')}
          </DialogTitle>
        </DialogHeader>
        {formAndFooter}
      </div>
      </DialogContent>
    </Dialog>
  );
}
